import { useParams, Link } from "react-router-dom";
import Header from "@/components/shared/Header";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { 
  BookOpen, 
  Network, 
  Search,
  ArrowRight,
  BookmarkPlus,
  Bookmark,
  Eye,
  Lightbulb,
  CheckCircle,
  Clock,
  Target
} from "lucide-react";
import { useDisciplineBySlug } from "@/hooks/useDisciplines";
import { useSubDisciplines } from "@/hooks/useSubDisciplines";
import { useNodes } from "@/hooks/useNodes";
import { useUserProgress } from "@/hooks/useUserProgress";
import { useCrossDisciplinaryEdges } from "@/hooks/useCrossDisciplinaryEdges";
import { useAuth } from "@/hooks/useAuth";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { getSubDisciplinesForDiscipline } from "@/lib/disciplineMapping";
import { DisciplineEntryPanel } from "@/components/learning/DisciplineEntryPanel";

// Phase 5 imports - simplified
import { useNavigation } from "@/components/shared/UnifiedNavigationTerminology";

const StudyPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { user } = useAuth();
  const { data: userProgress } = useUserProgress();
  const { preferences, updatePreferences } = useUserPreferences();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [isBookmarking, setIsBookmarking] = useState(false);

  // Phase 5 navigation
  const { currentMode, terminology, transitionTo } = useNavigation();

  // Get the current discipline by slug
  const { data: currentDiscipline, isLoading: disciplineLoading } = useDisciplineBySlug(slug || "");

  // Get sub-disciplines for this discipline
  const { data: dbSubDisciplines } = useSubDisciplines(currentDiscipline?.id);
  const staticSubDisciplines = currentDiscipline ? getSubDisciplinesForDiscipline(currentDiscipline.slug) : [];
  const subDisciplines = staticSubDisciplines.length > 0 ? staticSubDisciplines : dbSubDisciplines;

  // Get nodes for this discipline
  const { data: allNodes } = useNodes();
  const disciplineNodes = allNodes?.filter(node => 
    node.discipline_id === currentDiscipline?.id && 
    node.status === 'published'
  ) || [];

  // Get cross-disciplinary edges
  const { data: allEdges } = useCrossDisciplinaryEdges();
  const disciplineEdges = allEdges?.filter(edge => {
    const sourceInDiscipline = disciplineNodes.some(node => node.id === edge.source_node_id);
    const targetInDiscipline = disciplineNodes.some(node => node.id === edge.target_node_id);
    return sourceInDiscipline || targetInDiscipline;
  }) || [];

  // Get connected nodes from other disciplines
  const connectedNodes = allNodes?.filter(node => 
    disciplineEdges.some(edge => 
      (edge.source_node_id === node.id && node.discipline_id !== currentDiscipline?.id) ||
      (edge.target_node_id === node.id && node.discipline_id !== currentDiscipline?.id)
    )
  ) || [];

  // Calculate progress stats
  const disciplineProgress = userProgress?.filter(p => 
    disciplineNodes.some(node => node.id === p.node_id)
  ) || [];
  const engagedContent = disciplineProgress.filter(p => p.completed_at !== null).length;

  // Filter nodes based on search
  const filteredNodes = disciplineNodes.filter(node => {
    const contentString = typeof node.content === 'string' ? node.content : 
                         node.content ? JSON.stringify(node.content) : '';
    
    return searchTerm === "" || 
      node.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contentString.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Check if discipline is bookmarked
  const isBookmarked = () => {
    if (!user || !currentDiscipline) return false;
    const bookmarks = JSON.parse(localStorage.getItem(`bookmarks_${user.id}`) || '[]');
    return bookmarks.includes(currentDiscipline.id);
  };

  // Handle bookmark toggle
  const handleBookmarkToggle = async () => {
    if (!user || !currentDiscipline) return;
    
    setIsBookmarking(true);
    try {
      const bookmarks = JSON.parse(localStorage.getItem(`bookmarks_${user.id}`) || '[]');
      const isCurrentlyBookmarked = bookmarks.includes(currentDiscipline.id);
      
      let newBookmarks;
      if (isCurrentlyBookmarked) {
        newBookmarks = bookmarks.filter((id: string) => id !== currentDiscipline.id);
        toast({
          title: "Removed from bookmarks",
          description: `${currentDiscipline.name} has been removed from your followed disciplines.`,
        });
      } else {
        newBookmarks = [...bookmarks, currentDiscipline.id];
        toast({
          title: "Added to bookmarks",
          description: `${currentDiscipline.name} has been added to your followed disciplines.`,
        });
      }
      
      localStorage.setItem(`bookmarks_${user.id}`, JSON.stringify(newBookmarks));
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update bookmark. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsBookmarking(false);
    }
  };

  // Helper function to render content preview
  const renderContentPreview = (content: any) => {
    if (typeof content === 'string') {
      return content.substring(0, 150) + (content.length > 150 ? '...' : '');
    }
    if (content && typeof content === 'object') {
      const text = JSON.stringify(content).replace(/[{}"\[\]]/g, ' ');
      return text.substring(0, 150) + (text.length > 150 ? '...' : '');
    }
    return 'No preview available';
  };

  // Enhanced node card
  const renderNodeCard = (node: any) => {
    const nodeProgress = disciplineProgress.find(p => p.node_id === node.id);
    const isCompleted = nodeProgress?.completed_at !== null;
    
    return (
      <Link key={node.id} to={`/content/view/${node.id}`}>
        <Card className="h-full group cursor-pointer transition-all hover:shadow-md">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <CardTitle className="text-lg group-hover:text-primary transition-colors line-clamp-2">
                  {node.title}
                </CardTitle>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="outline" className="text-xs">
                    {node.node_type}
                  </Badge>
                  {isCompleted && (
                    <Badge variant="secondary" className="text-xs bg-white text-green-700">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Completed
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-black line-clamp-3 mb-3">
              {renderContentPreview(node.content)}
            </p>
            <div className="flex items-center justify-between text-xs text-black">
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                <span>5-10 min read</span>
              </div>
              <div className="flex items-center gap-1">
                <Eye className="w-3 h-3" />
                <span>{node.view_count || 0} views</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </Link>
    );
  };

  if (disciplineLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-black">Loading...</p>
        </div>
      </div>
    );
  }

  if (!currentDiscipline) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <Alert>
            <AlertDescription>
              Discipline not found. Please check the URL or return to the homepage.
            </AlertDescription>
          </Alert>
          <Button asChild className="mt-4">
            <Link to="/">Return Home</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Page Header */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">{currentDiscipline.name}</h1>
              <p className="text-black mt-1">{currentDiscipline.description}</p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleBookmarkToggle}
                disabled={isBookmarking}
              >
                {isBookmarked() ? <Bookmark className="w-4 h-4" /> : <BookmarkPlus className="w-4 h-4" />}
                {isBookmarked() ? 'Following' : 'Follow'}
              </Button>
              {user && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => transitionTo(currentMode === 'learning' ? 'contributing' : 'learning')}
                >
                  {currentMode === 'learning' ? 'Contribute' : terminology.actions.start}
                </Button>
              )}
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-black" />
            <Input
              placeholder={`Search ${currentDiscipline.name} content...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Smart Start Here Panel - Prominent Placement */}
        {user && (
          <DisciplineEntryPanel
            disciplineId={currentDiscipline.id}
            disciplineName={currentDiscipline.name}
            disciplineDescription={currentDiscipline.description}
            totalNodes={disciplineNodes.length}
            userCompletedNodes={engagedContent}
            onStartRecommendation={(nodeId) => {
              window.location.href = `/content/view/${nodeId}`;
            }}
          />
        )}

        {/* Progress Overview - For logged out users or as secondary info */}
        {!user && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5" />
                Explore {currentDiscipline.name}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold">{disciplineNodes.length}</div>
                  <div className="text-sm text-black">Total {terminology.content.plural}</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-black">
                    {Math.round(disciplineNodes.length * 0.2)}
                  </div>
                  <div className="text-sm text-black">Entry Points Available</div>
                </div>
              </div>
              <div className="mt-4 text-center">
                <p className="text-sm text-black mb-3">
                  Sign in to get personalized recommendations and track your progress
                </p>
                <Button asChild>
                  <Link to="/auth/login">Sign In for Personalized Experience</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Content Tabs */}
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="content">{terminology.content.plural}</TabsTrigger>
            <TabsTrigger value="connections">Connections</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6">
            <div className="grid gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>About {currentDiscipline.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-black mb-4">{currentDiscipline.description}</p>
                  {subDisciplines.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-2">Key Areas:</h4>
                      <div className="grid gap-2">
                        {subDisciplines.slice(0, 3).map((sub, index) => (
                          <div key={index} className="text-sm">
                            <span className="font-medium">{sub.title || sub.name}</span>
                            {sub.description && (
                              <span className="text-black"> - {sub.description}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Smart Start Here Guidance */}
              <DisciplineEntryPanel
                disciplineId={currentDiscipline.id}
                disciplineName={currentDiscipline.name}
                disciplineDescription={currentDiscipline.description}
                totalNodes={disciplineNodes.length}
                userCompletedNodes={engagedContent}
                onStartRecommendation={(nodeId) => {
                  window.location.href = `/content/view/${nodeId}`;
                }}
              />
            </div>
          </TabsContent>

          <TabsContent value="content" className="mt-6">
            <div className="space-y-6">
              {filteredNodes.length === 0 ? (
                <Card>
                  <CardContent className="text-center py-8">
                    <p className="text-black">No content matches your search.</p>
                    <Button 
                      variant="outline" 
                      onClick={() => setSearchTerm('')}
                      className="mt-2"
                    >
                      Clear Search
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {filteredNodes.map(renderNodeCard)}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="connections" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Network className="w-5 h-5" />
                  Cross-Disciplinary Connections
                </CardTitle>
                <CardDescription>
                  Explore how {currentDiscipline.name} connects with other fields of study
                </CardDescription>
              </CardHeader>
              <CardContent>
                {connectedNodes.length > 0 ? (
                  <div className="grid gap-3">
                    {connectedNodes.slice(0, 5).map(node => (
                      <Link key={node.id} to={`/content/view/${node.id}`}>
                        <div className="p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                          <div className="flex items-start justify-between">
                            <div>
                              <h5 className="font-medium">{node.title}</h5>
                              <p className="text-sm text-black">
                                From {node.disciplines?.name}
                              </p>
                            </div>
                            <ArrowRight className="w-4 h-4 text-black" />
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-black">
                    No cross-disciplinary connections found yet.
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default StudyPage; 